import { IkeDate, IkeDateFormat } from '@ikegps/core-shared';
import { debounce } from 'lodash';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { InputState } from './input.state';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Editable input state for date fields. The value is a UTC-anchored `Date`
 * (midnight UTC of the calendar day), while the user sees and edits it in their
 * configured date format (@see Services.UserSettings.dateFormat, e.g. MM/DD/YYYY
 * or DD/MM/YYYY).
 *
 * A stored date is a calendar day, not an instant, so the Date is anchored to
 * UTC and only ever read/written through its UTC components — the local timezone
 * can never shift the day. Formatting and parsing follow the user's date-format
 * setting rather than the host locale.
 *
 * The model stores dates as yyyy-MM-dd strings, so consumers bridge across that
 * boundary with `isoValue` / `setIsoValue`.
 */
export class DateInputState extends InputState<Date> {
	/**
	 * Raise the format error a beat after the user stops typing (not on every
	 * keystroke), and flush it eagerly on blur (@see onBlur). 600ms mirrors the
	 * debounce in @see DistanceInputState.
	 */
	private readonly debouncedValidate = debounce(() => this.validate(), 600);

	constructor(label: string) {
		super(label);

		// Re-interpret the input when the user changes their date format setting.
		// The placeholder shows the format itself, so it tracks the setting
		// directly. A valid value is unchanged — only its presentation — so it is
		// re-displayed in the new format; pending (unparsed) text is re-parsed and
		// re-validated against the new format. This runs setValue/setInputValue
		// (which only fire the onValue reaction when the value actually changes)
		// rather than mutating the display during render.
		this.reaction(
			() => Services.UserSettings.dateFormat,
			(dateFormat) => {
				this.setPlaceholder(dateFormat);
				if (this.focused) {
					return;
				}
				if (this.value != null || this.inputValue == null || this.inputValue.trim() === '') {
					this.setValue(this.value);
				} else {
					this.setInputValue(this.inputValue);
				}
				this.validate();
			}
		);

		// Clear the error the instant the input resolves to a real date — whether
		// the user typed it or chose it from the picker. Raising the error is the
		// job of onBlur / the debounce; this is the single eager-clear path.
		this.reaction(
			() => this.value,
			(value) => {
				if (value != null) {
					this.setError(null);
				}
			}
		);
	}

	/**
	 * Flag unparseable input with the expected format. Parsing already happens on
	 * every keystroke (@see parseInputValue), so a non-empty input with no parsed
	 * value is text the user typed that does not match their format.
	 */
	private validate() {
		const raw = this.inputValue;
		if (raw != null && raw.trim() !== '' && this.value == null) {
			this.setError(Translate(T.DATE_FORMAT_EXPECTED, { format: Services.UserSettings.dateFormat }));
		} else {
			this.setError(null);
		}
	}

	/** Re-validate a beat after the user stops typing. */
	override onInput() {
		this.debouncedValidate();
	}

	/** Blur is the definitive "finished editing" signal — validate now, not after the debounce. */
	override onBlur() {
		this.debouncedValidate.cancel();
		this.validate();
	}

	override destroy() {
		this.debouncedValidate.cancel();
		super.destroy();
	}

	/** Parse a stored yyyy-MM-dd string into a UTC-anchored Date, or null. */
	private static fromIso(value: string | null): Date | null {
		const match = value != null ? ISO_DATE.exec(value) : null;
		if (match == null) {
			return null;
		}

		const [, year, month, day] = match;
		return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
	}

	/** The value as a yyyy-MM-dd string for the model boundary, or null. */
	get isoValue(): string | null {
		return this.value != null ? IkeDate.getUtcYearMonthDay(this.value) : null;
	}

	/** Set the value from a stored yyyy-MM-dd string (null/invalid clears it). */
	setIsoValue(value: string | null) {
		this.setValue(DateInputState.fromIso(value));
	}

	/** Value formatted for a native `<input type="date">` (yyyy-MM-dd), or '' when unset. */
	get nativeValue(): string {
		return this.isoValue ?? '';
	}

	override formatInputValue(value: Date | null): string | null {
		// A date field is a calendar day anchored to UTC midnight, so format its
		// UTC components (timeZone: 'UTC') — the local zone must never shift the day.
		return value == null ? null : IkeDateFormat.short(value, Services.UserSettings.dateFormat, { timeZone: 'UTC' });
	}

	override parseInputValue(value: string): Date | null {
		return IkeDateFormat.parse(value, Services.UserSettings.dateFormat);
	}
}
